/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";
import DropdownCheckbox from "./DropdownCheck";
import { DateInput } from "./Input";

const EXCLUDED_KEYS = [
  "preAcceptDate",
  "sdPublishedOnWebS300",
  "sceReturnedStatus",
  "itemHoldout",
  "pressRelease",
  "articleS300Date",
  "copyEditTaskComplete",
  "cfcTaskComplete",
  "coverSubReceived",
  "issueS300Date",
];
const DynamicFilterPopover = ({
  sections = [],
  tempSelected = {},
  setTempSelected = {},
  clearAllFilters,
  applyFilters,
  type = "Article",
  productionWorkflowOpen,
  setProductionWorkflowOpen,
  pitOpen,
  setPitOpen,
  docHeadOpen,
  setDocHeadOpen,
  pointsOpen,
  setPointsOpen,
  maxHeight = null,
  width = "w-[324px]",
  innerWidth = "w-[290px]",
}) => {
  const formRef = useRef(null);

  useEffect(() => {
    const formElement = formRef.current;

    const handleScroll = () => {
      if (productionWorkflowOpen) {
        setProductionWorkflowOpen(false);
      } else if (pitOpen) {
        setPitOpen(false);
      } else if (docHeadOpen) {
        setDocHeadOpen(false);
      } else if (pointsOpen) {
        setPointsOpen(false);
      }
    };

    if (formElement) {
      formElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (formElement) {
        formElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [productionWorkflowOpen, pitOpen, docHeadOpen, pointsOpen]);

  const totalSelected = sections.reduce((sum, section) => {
    if (section.key === "onDate") {
      const selected = tempSelected[section.key];
      return sum + (selected?.from || selected?.to ? 1 : 0);
    }
    return EXCLUDED_KEYS.includes(section.key)
      ? sum + (tempSelected[section.key] !== "" ? 1 : 0)
      : type === "Article"
      ? sum +
        (tempSelected[section.key]?.length &&
        tempSelected[section.key][0] !== "All"
          ? 1
          : 0)
      : sum +
        (tempSelected[section.key]?.length &&
        tempSelected[section.key][0] !== "All"
          ? tempSelected[section.key]?.length
          : 0);
  }, 0);

  const totalOptions =
    type === "Article" ? sections.length : sections?.[0]?.values?.length;

  const handleOpen = (key, open) => {
    switch (key) {
      case "productionWorkflow":
        setProductionWorkflowOpen(open);
        break;
      case "pit":
        setPitOpen(open);
        break;
      case "docHead":
        setDocHeadOpen(open);
        break;
      case "points":
        setPointsOpen(open);
        break;
      default:
        break;
    }
  };

  const renderYesNoButtons = (key) => (  
    <div className="flex gap-1">
      {[true, false].map((val) => (
        <button
          key={val ? "yes" : "no"}
          className={`px-3 py-1 rounded-full h-[24px] w-[60px] text-[13px] justify-center font-semibold border flex items-center ${
            tempSelected[key] === val
              ? "bg-[#E1E6FF] text-[#1C40C9] border-[#1C40C9]"
              : "bg-white text-[#1C40C9] border-[#1C40C9]"
          }`}
          onClick={() => setTempSelected[key](val)}
        >
          {tempSelected[key] === val && (
            <FaCheck className="mr-1 text-[#1C40C9] text-xs" />
          )}
          {val ? "Yes" : "No"}
        </button>
      ))}
    </div>
  );

  const getOpenState = (key) => {
    switch (key) {
      case "productionWorkflow":
        return productionWorkflowOpen;
      case "pit":
        return pitOpen;
      case "docHead":
        return docHeadOpen;
      case "points":
        return pointsOpen;
      default:
        return false;
    }
  };

  const handleDateChange = (key, field, value) => {
    setTempSelected[key](field, value);
  };

  return (
    <PopoverContent
      className={`${width} max-h-[450px] bg-white border border-[#C2C6CA] rounded-md shadow-lg z-50 p-4`}
      align="end"
      sideOffset={5}
      style={{ maxHeight: `${maxHeight || 450}px` }}
    >
      <style>
        {`
          /* Hide scrollbar for Chrome, Safari and Opera */
          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          /* Hide scrollbar for IE, Edge and Firefox */
          .no-scrollbar {
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
          }
        `}
      </style>
      <div
        className="flex flex-col gap-1"
        style={{ maxHeight: `${(maxHeight || 450) - 32}px` }}
      >
        <div
          className="flex flex-col gap-1 overflow-auto no-scrollbar"
          style={{ height: "calc(100% - 48.5px)" }}
          ref={formRef}
        >
          {sections.map((section, index) => {
            const isYesNo = EXCLUDED_KEYS.includes(section.key);
            const open = getOpenState(section.key);

            if (section.key === "onDate") {
              return (
                <div key={section.key} className="flex flex-col">
                  <label className="text-[13px] font-medium text-[#35424D]">
                    {section.label}
                  </label>
                  <div className="flex gap-2">
                    <DateInput
                      name={`${section.key}-from`}
                      value={tempSelected[section.key]?.from || ""}
                      onChange={(e) =>
                        handleDateChange(section.key, "from", e.target.value)
                      }
                    />
                    <DateInput
                      name={`${section.key}-to`}
                      value={tempSelected[section.key]?.to || ""}
                      onChange={(e) =>
                        handleDateChange(section.key, "to", e.target.value)
                      }
                    />
                  </div>
                  {index < sections.length - 1 && (
                    <hr className="border-t border-gray-300 my-2" />
                  )}
                </div>
              );
            }

            // === Yes/No Section ===
            if (isYesNo) {
              return (
                <div key={section.key}>
                  <h3 className="text-[13px] font-medium mb-2 text-[#35424D]">
                    {section.label}
                  </h3>
                  {renderYesNoButtons(section.key)}
                  {index < sections.length - 1 && (
                    <hr className="border-t border-gray-300 my-2" />
                  )}
                </div>
              );
            }

            // === Article Dropdown Section ===
            if (type === "Article") {
              return (
                <div key={section.key}>
                  <h3 className="text-[13px] font-medium mb-2 text-[#35424D]">
                    {section.label}
                  </h3>
                  <Popover
                    open={open}
                    onOpenChange={(val) => handleOpen(section.key, val)}
                  >
                    <PopoverTrigger asChild>
                      <div className="flex w-full justify-between border border-[#1C40C9] text-[#1C40C9] px-2 py-1 rounded-md items-center">
                        <button className="text-[13px] focus:outline-none text-left truncate w-[220px]">
                          {(() => {
                            const selected = tempSelected[section.key] || [];
                            if (selected.length === 0)
                              return `Select ${section.label.toLowerCase()}`;
                            const firstLabel =
                              section.values.find(
                                (v) => v.value === selected[0]
                              )?.label || selected[0];
                            const remaining = selected.length - 1;
                            return remaining > 0
                              ? `${firstLabel} + ${remaining} more`
                              : firstLabel;
                          })()}
                        </button>
                        {open ? (
                          <FaChevronUp className="text-[13px]" />
                        ) : (
                          <FaChevronDown className="text-[13px]" />
                        )}
                      </div>
                    </PopoverTrigger>
                    <DropdownCheckbox
                      options={section.values || []}
                      selectedValues={tempSelected[section.key] || []}
                      selectAllText="All"
                      clearAllText="Clear all"
                      applyText="Apply"
                      isOpen={open}
                      searchPlaceholder={`Search ${section.label.toLowerCase()}...`}
                      onSelectionChange={(value) =>
                        setTempSelected[section.key](value)
                      }
                      onClose={() => handleOpen(section.key, false)}
                      width={innerWidth}
                      height="max-h-[350px]"
                      innerHeight="max-h-[150px]"
                    />
                  </Popover>
                  {index < sections.length - 1 && (
                    <hr className="border-t border-gray-300 my-2" />
                  )}
                </div>
              );
            }

            // === Issue / fallback Section ===
            return (
              <div key={section.key}>
                <h3 className="text-[13px] font-medium mb-2 text-[#35424D]">
                  {section.label}
                </h3>
                <div className="flex gap-2 flex-wrap">
                  {section.values?.map((option) => (
                    <button
                      key={`${section.key}-${option.value}`}
                      className={`px-3 py-1 rounded-full h-[24px] text-[13px] font-semibold border flex items-center ${
                        tempSelected[section.key]?.includes(option.value)
                          ? "bg-[#E1E6FF] text-[#1C40C9] border-[#1C40C9]"
                          : "bg-white text-[#1C40C9] border-[#1C40C9]"
                      }`}
                      onClick={() => {
                        const currentValues = tempSelected[section.key] || [];
                        const newValues = currentValues.includes(option.value)
                          ? currentValues.filter((v) => v !== option.value)
                          : [...currentValues, option.value];
                        setTempSelected[section.key](newValues);
                      }}
                    >
                      {tempSelected[section.key]?.includes(option.value) && (
                        <FaCheck className="mr-1 text-[#1C40C9] text-xs" />
                      )}
                      {option.label}
                    </button>
                  ))}
                </div>
                {index < sections.length - 1 && (
                  <hr className="border-t border-gray-300 my-2" />
                )}
              </div>
            );
          })}
        </div>

        <hr className="border-t border-gray-300" />
        <div className="flex justify-between items-center">
          <p className="text-[11px] text-[#35424D]">
            {totalSelected} of {totalOptions} Selected
          </p>
          <button
            className="text-[#1C40C9] text-[11px] font-semibold h-[16px] underline focus:outline-none"
            onClick={clearAllFilters}
          >
            Clear all
          </button>
        </div>

        <hr className="border-t border-gray-300 mb-1" />
        <button
          className="w-full bg-[#04239A] hover:bg-[#1C40C9] h-[32px] text-white rounded-md"
          onClick={applyFilters}
        >
          <span className="text-[13px] font-semibold">Apply</span>
        </button>
      </div>
    </PopoverContent>
  );
};

export default DynamicFilterPopover;
